import {
  DEFAULT_FIRST_SEGMENT_COLOR,
  DEFAULT_LAST_SEGMENT_COLOR,
  DEFAULT_PADDING,
  DEFAULT_SEGMENT_COLORS,
} from './constants';
import type {
  BarChartData,
  CategoryConfig,
  ChartDimensions,
  SegmentData,
  SegmentValue,
  YScaleResult,
} from './types';

export const easeOut = (t: number): number => {
  const clamped = Math.max(0, Math.min(1, t));

  return 1 - (1 - clamped) ** 3;
};

export const calculateChartDimensions = (
  width: number,
  height: number
): ChartDimensions => {
  if (!Number.isFinite(width) || width <= 0) {
    throw new Error('Invalid width provided to calculateChartDimensions');
  }

  if (!Number.isFinite(height) || height <= 0) {
    throw new Error('Invalid height provided to calculateChartDimensions');
  }

  const padding = DEFAULT_PADDING;
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  return {
    width,
    height,
    chartWidth,
    chartHeight,
    chartTop: padding.top,
    chartBottom: height - padding.bottom,
    chartLeft: padding.left,
    chartRight: width - padding.right,
    padding,
  };
};

/**
 * Generates nice tick intervals dynamically based on data range
 * Uses the "nice numbers" algorithm to find human-readable intervals
 *
 * @param range - The data range (max - min)
 * @returns A nice tick interval value
 */
const getNiceTickInterval = (range: number): number => {
  if (range === 0) {
    return 1;
  }

  // Calculate the order of magnitude
  const exponent = Math.floor(Math.log10(range));
  const mantissa = range / 10 ** exponent;

  // Choose a nice interval based on mantissa
  let niceMantissa: number;

  if (mantissa < 1.5) {
    niceMantissa = 1;
  } else if (mantissa < 3) {
    niceMantissa = 2;
  } else if (mantissa < 7) {
    niceMantissa = 5;
  } else {
    niceMantissa = 10;
  }

  return niceMantissa * 10 ** exponent;
};

export const calculateYScale = (
  maxTotal: number,
  chartHeight: number
): YScaleResult => {
  if (!Number.isFinite(maxTotal) || maxTotal < 0) {
    throw new Error('Invalid maxTotal provided to calculateYScale');
  }

  if (!Number.isFinite(chartHeight) || chartHeight <= 0) {
    throw new Error('Invalid chartHeight provided to calculateYScale');
  }

  if (maxTotal === 0) {
    return {
      scale: () => 0,
      maxValue: 10,
      tickInterval: 2,
      tickCount: 2,
    };
  }

  // Generate nice tick interval dynamically
  const roughInterval = maxTotal / 4;
  const tickInterval = getNiceTickInterval(roughInterval);

  const maxValue = Math.ceil(maxTotal / tickInterval) * tickInterval;
  const tickCount = Math.floor(maxValue / tickInterval) + 1;
  const pixelsPerUnit = chartHeight / maxValue;

  const scale = (value: number): number => {
    const clamped = Math.max(0, Math.min(maxValue, value));

    return clamped * pixelsPerUnit;
  };

  return {
    scale,
    maxValue,
    tickInterval,
    tickCount,
  };
};

export const calculateSegments = (
  segmentValues: SegmentValue[],
  categories: Map<string, CategoryConfig>,
  yScale: (value: number) => number,
  segmentColors?: string[]
): SegmentData[] => {
  // Filter out zero-value segments
  const validSegments = segmentValues.filter((seg) => seg.value > 0);

  // Calculate cumulative heights
  let cumulativeHeight = 0;
  const segments: SegmentData[] = [];
  const totalSegments = validSegments.length;

  validSegments.forEach((segmentValue, categoryIndex) => {
    const { categoryId, value } = segmentValue;

    const category = categories.get(categoryId);

    if (!category) {
      return;
    }

    // Calculate segment height using yScale
    const height = yScale(value);

    // Determine if this is first or last segment
    const isFirstSegment = categoryIndex === 0;
    const isLastSegment = categoryIndex === totalSegments - 1;

    // Priority: segmentColors array by index > first/last defaults > category.color > default palette
    let color: string;

    if (segmentColors?.[categoryIndex]) {
      color = segmentColors[categoryIndex];
    } else if (isFirstSegment) {
      color = DEFAULT_FIRST_SEGMENT_COLOR;
    } else if (isLastSegment) {
      color = DEFAULT_LAST_SEGMENT_COLOR;
    } else if (category.color) {
      color = category.color;
    } else {
      color =
        DEFAULT_SEGMENT_COLORS[categoryIndex % DEFAULT_SEGMENT_COLORS.length];
    }

    const segment: SegmentData = {
      categoryId,
      label: category.label,
      value,
      color,
      cumulativeHeight,
      height,
    };

    segments.push(segment);

    cumulativeHeight += height;
  });

  return segments;
};

export const extractCategoriesFromData = (
  data: BarChartData[],
  segmentColors?: string[]
): CategoryConfig[] => {
  const categoryMap = new Map<string, { label: string; index: number }>();
  let categoryIndex = 0;

  // Collect all unique categories from data
  data.forEach((item) => {
    item.segments.forEach((segment) => {
      if (!categoryMap.has(segment.categoryId)) {
        // Use categoryId as label, can be customized by user later
        categoryMap.set(segment.categoryId, {
          label: segment.categoryId,
          index: categoryIndex,
        });
        categoryIndex += 1;
      }
    });
  });

  // Convert to CategoryConfig array
  return Array.from(categoryMap.entries()).map(([id, { label, index }]) => ({
    id,
    label,
    color: segmentColors?.[index],
  }));
};

export const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};
